import { Dictionary, groupBy, isNil, map, values } from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { View } from '@shared/components/View';
import { MBHover } from '@shared/components/hover';
import { MBIconV2, MBImage } from '@shared/components/image';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody2 } from '@shared/components/typography';
import { MBSeenBy } from '@shared/scenes/chat/thread/components/seenBy';
import { useGetChannelEventHandlers } from '@shared/scenes/workflows/hooks';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { I18nKey, i18n } from '@shared/util/i18n';
import { UnvalidatedStyles as BubbleStyles } from '../../bubble/styles';
import { MBBaseSystemMessage } from '../base';
import { getIcon, getIconColor } from '../systemMessageCardUtils';

interface Props {
  isDesktop: boolean;
  messages: API.ChatMessageFragment[];
  layout: MBChatMessageLayoutOptions;
  anchorName: string;
}

export function MBSystemMessageGroup({ messages, layout, isDesktop, anchorName }: Props) {
  const oldestToNewestMessages = useMemo(() => messages.slice().reverse(), [messages]);
  const colors = useContext(ColorThemeContext);
  const grouped = useMemo(
    () => groupBy(oldestToNewestMessages, 'subType'),
    [oldestToNewestMessages],
  );
  const [isExpanded, , , toggleExpanded] = useBoolean(false);

  const summary = map(grouped, (messageGroup, subType) =>
    i18n(`chat.groupedSystemMessages.${subType}` as I18nKey, { count: messageGroup.length }),
  ).join(', ');

  return (
    <View paddingVertical={1} paddingLeft={2.25}>
      <View row centerVertically paddingRight={2} style={{ justifyContent: 'space-between' }}>
        <MBTouchableOpacity onPress={toggleExpanded}>
          <View row centerVertically>
            <GroupedSystemMessageIcons grouped={grouped} />
            <View flex style={{ overflow: 'hidden', maxWidth: '100%' }}>
              <MBTextBody2 color={colors.foregroundActive} numberOfLines={1}>
                {summary}
              </MBTextBody2>
            </View>
            <View paddingLeft={1}>
              <MBIconV2
                size={12}
                color={colors.foregroundActive}
                name={isExpanded ? 'arrow-point-up-heavy' : 'arrow-point-down-heavy'}
              />
            </View>
          </View>
        </MBTouchableOpacity>
      </View>
      {isExpanded && (
        <View paddingTop={1} paddingLeft={3}>
          {oldestToNewestMessages.map(message => (
            <SystemMessageGroupItem key={message.id} message={message} isDesktop={isDesktop} />
          ))}
        </View>
      )}
      <View paddingTop={0.5} paddingLeft={5}>
        <MBSeenBy users={layout.seenByUsers} anchorName={anchorName} align="left" />
      </View>
    </View>
  );
}

function GroupedSystemMessageIcons({
  grouped,
}: {
  grouped: Dictionary<API.ChatMessageFragment[]>;
}) {
  return (
    <View
      row
      paddingRight={1}
      style={{ width: BubbleStyles.senderImageSize, justifyContent: 'flex-end' }}
    >
      {values(grouped).map((messageGroup, index) => (
        <MBImage
          key={index}
          tintColor={getIconColor(messageGroup[0])}
          source={getIcon(messageGroup[0])}
          style={[styles.icon, index !== 0 && styles.iconOverlap]}
        />
      ))}
    </View>
  );
}

function SystemMessageGroupItem({
  message,
  isDesktop,
}: {
  message: API.ChatMessageFragment;
  isDesktop: boolean;
}) {
  const [isHovered, onHoverStart, onHoverEnd] = useBoolean(!isDesktop); // treat hovered as true on mobile
  const getChannelEventHandlers = useGetChannelEventHandlers();
  const cardId = message.meta?.cardId;

  const isPresssable = message.subType === 'CARD_CREATED' && !isNil(cardId);
  const onPress = useCallback(async () => {
    if (isPresssable) {
      const { onOpenCard, foundCard } = await getChannelEventHandlers(cardId);
      onOpenCard(foundCard);
    }
  }, [cardId, getChannelEventHandlers, isPresssable]);

  return (
    <MBHover {...{ onHoverStart, onHoverEnd }}>
      <MBTouchableOpacity onPress={isPresssable ? onPress : undefined}>
        <MBBaseSystemMessage
          icon={getIcon(message as any)}
          color={getIconColor(message as any)}
          text={message.content}
          date={message.createdAt}
          hovering={isHovered}
          isDesktop={isDesktop}
        />
      </MBTouchableOpacity>
    </MBHover>
  );
}

const borderWidth = 2;
const styles = StyleSheet.create({
  icon: {
    width: grid(2) + 2 * borderWidth,
    height: grid(2) + 2 * borderWidth,
    borderRadius: grid(1) + borderWidth,
    overflow: 'hidden',
    backgroundColor: 'white',
    borderColor: 'white',
    borderWidth,
  },
  iconOverlap: {
    marginLeft: -grid(1),
  },
});
